import { Alert } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import JiraIcon from '../../assets/images/export-jira-icon.svg';
import { Dialog, DialogBody, DialogButtons } from '../../common/components/Modal/Modal';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/Buttons';
import { FormSummary } from '../components/FormSummary';
import { ProjectForm } from '../components/ProjectForm';
import { SlotForm } from '../components/SlotForm';
import { TraceForm } from '../components/TraceForm';
import { Project } from '../models/Project';
import { Report } from '../models/Report';
import { Slot } from '../models/Slot';
import { SlotType } from '../models/SlotType';
import { ProjectService } from '../services/ProjectService';
import { SlotService } from '../services/SlotService';

enum Steps {
  PROJECT_DATA = 1,
  PROJECT_CREATED = 2,
  SLOT_DATA = 3,
  SLOT_CREATED = 4,
  UPLOAD_TRACE = 5,
  REPORT_CREATED = 6,
}

type Props = {
  slotTypes: SlotType[];
  hasUnlimitedReports?: boolean;
  onClosed: (
    dismissed: boolean,
    projectId: number | undefined,
    slotId: number | undefined,
    reportId: number | undefined,
    integrationsClicked?: boolean
  ) => void;
};

export const CreateProjectFlow = ({ slotTypes, hasUnlimitedReports = false, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [projectService] = useState<ProjectService>(new ProjectService());
  const [slotService] = useState<SlotService>(new SlotService());

  useEffect(() => {
    // Cancel all active requests on unmount
    return () => {
      projectService.cancelAllRequests();
      slotService.cancelAllRequests();
    };
  }, [projectService, slotService]);

  const [step, setStep] = useState<Steps>(Steps.PROJECT_DATA);
  const [project, setProject] = useState<Project | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const onProjectCreated = (p: Project) => {
    setProject(p);
    setStep(Steps.PROJECT_CREATED);
  };

  const onAddSlot = () => {
    setStep(Steps.SLOT_DATA);
  };

  const onSlotCreated = (s: Slot) => {
    setSlot(s);
    setStep(Steps.SLOT_CREATED);
  };

  const onCreateAnotherSlot = () => {
    setSlot(null);
    setReport(null);
    setStep(Steps.SLOT_DATA);
  };

  const onUploadTrace = () => {
    setStep(Steps.UPLOAD_TRACE);
  };

  const onReportCreated = useCallback((r: Report) => {
    setReport(r);
    setStep(Steps.REPORT_CREATED);
  }, []);

  const STEP_MAP = {
    [Steps.PROJECT_DATA]: {
      title: t('createProject.project.title'),
      subtitle: t('createProject.project.subtitle'),
      component: () => (
        <ProjectForm
          unlimitedReports={hasUnlimitedReports}
          submit={{
            label: t('createProject.project.submit'),
            action: (name, description, profileId, maxItemCount) =>
              projectService.createProject(name, description, profileId, maxItemCount).then(onProjectCreated),
          }}
          back={{ label: t('createProject.project.back'), action: () => onClosed(false, undefined, undefined, undefined) }}
        />
      ),
      actions: null,
    },
    [Steps.PROJECT_CREATED]: {
      title: t(`createProject.projectDone.title`, { projectName: project?.getName() }),
      subtitle: t(`createProject.projectDone.slot.integrationsEnabled.subtitle`),
      component: () => (
        <>
          <FormSummary project={project} />
          <Alert sx={{ mt: 2, alignItems: 'center' }} color="info" icon={<JiraIcon aria-hidden height="31" width="31" />}>
            <Interpolation i18nKey="createProject.projectDone.tip" />
          </Alert>
        </>
      ),
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(false, project?.getId(), undefined, undefined)}>{t(`createProject.projectDone.back`)}</TertiaryButton>
          <SecondaryButton onClick={() => onClosed(false, project?.getId(), undefined, undefined, true)}>
            {t('createProject.projectDone.integrations')}
          </SecondaryButton>
          <PrimaryButton autoFocus onClick={onAddSlot}>
            {t(`createProject.projectDone.slot.submit`)}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
    [Steps.SLOT_DATA]: {
      title: t(`createProject.slot.slot.title`),
      subtitle: t(`createProject.slot.slot.subtitle`),
      component: () => (
        <SlotForm
          submit={{
            label: t(`createProject.slot.slot.submit`),
            action: (name, description, slotType, tags) => slotService.createSlot(project!.getId(), name, description, slotType, tags).then(onSlotCreated),
          }}
          back={{ label: t(`createProject.slot.back`), action: () => onClosed(false, project?.getId(), undefined, undefined) }}
          slotTypes={slotTypes}
        />
      ),
      actions: null,
    },
    [Steps.SLOT_CREATED]: {
      title: t(`createProject.slotDone.slot.title`, { slotName: slot?.getName() }),
      subtitle: t(`createProject.slotDone.slot.subtitle`),
      component: () => <FormSummary project={project} slot={slot} />,
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(false, project?.getId(), slot?.getId(), undefined)}>{t(`createProject.slotDone.back`)}</TertiaryButton>
          <SecondaryButton onClick={onCreateAnotherSlot}>{t(`createProject.slotDone.slot.another`)}</SecondaryButton>
          <PrimaryButton autoFocus onClick={onUploadTrace}>
            {t(`createProject.slotDone.submit`)}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
    [Steps.UPLOAD_TRACE]: {
      title: t(`createProject.upload.title`),
      subtitle: t(`createProject.upload.slot.subtitle`),
      component: () => <TraceForm projectId={project!.getId()} slotId={slot!.getId()} onDone={onReportCreated} />,
      actions: null,
    },
    [Steps.REPORT_CREATED]: {
      title: t('createProject.uploadDone.title', { reportName: report?.getName() }),
      subtitle: t('createProject.uploadDone.subtitle'),
      component: () => <FormSummary project={project} slot={slot} report={report} />,
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(true, project?.getId(), slot?.getId(), report?.getId())}>{t('createProject.uploadDone.back')}</TertiaryButton>
          <PrimaryButton autoFocus onClick={() => onClosed(false, project?.getId(), slot?.getId(), report?.getId())}>
            {t('createProject.uploadDone.submit')}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
  };

  return (
    <Dialog title={STEP_MAP[step].title} subtitle={STEP_MAP[step].subtitle} onDismiss={() => onClosed(true, project?.getId(), slot?.getId(), report?.getId())}>
      <DialogBody>{STEP_MAP[step].component()}</DialogBody>
      {STEP_MAP[step].actions?.()}
    </Dialog>
  );
};
